"use client"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Github, Linkedin, Mail, Send, ArrowRight } from "lucide-react"
import { motion } from "framer-motion"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

const socialLinks = [
  { name: "Email", icon: <Mail className="h-5 w-5" />, href: "mailto:sakthiish22@gmail.com" },
  { name: "Work Email", icon: <Mail className="h-5 w-5" />, href: "mailto:sakthiish.vijayadass@nice.com" },
  { name: "LinkedIn", icon: <Linkedin className="h-5 w-5" />, href: "https://www.linkedin.com/in/sakthiish-vijayadass/" },
  { name: "GitHub", icon: <Github className="h-5 w-5" />, href: "https://github.com/sakthiish12" },
  { name: "Telegram", icon: <Send className="h-5 w-5" />, href: "https://t.me/sakeboi" },
]

interface FormData {
  name: string
  email: string
  subject: string
  message: string
}

export default function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormData>()

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    
    try {
      // Send form data to our API endpoint
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }
      
      toast.success("Message sent successfully! I'll get back to you soon.")
      reset()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to send message. Please try again later.")
      console.error("Contact form submission error:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <footer id="contact" className="section-padding border-t" aria-labelledby="contact-heading">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 
            id="contact-heading"
            className="heading-responsive font-semibold mb-6 font-display"
          >
            Let&apos;s Connect
          </h2>
          <p className="text-responsive text-muted-foreground mb-4 max-w-xl mx-auto">
            Looking for collaborators, clients, or new opportunities? Feel free to reach out!
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="bg-card rounded-xl p-6 shadow-md border border-border/80"
          >
            <h3 className="text-xl font-semibold mb-4">Send Me a Message</h3>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="Your name"
                  {...register("name", { required: "Name is required" })}
                  aria-describedby={errors.name ? "name-error" : undefined}
                  aria-invalid={errors.name ? "true" : "false"}
                />
                {errors.name && (
                  <p id="name-error" className="text-sm text-destructive">{errors.name.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address"
                    }
                  })}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  aria-invalid={errors.email ? "true" : "false"}
                />
                {errors.email && (
                  <p id="email-error" className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  placeholder="What is this regarding?"
                  {...register("subject", { required: "Subject is required" })}
                  aria-describedby={errors.subject ? "subject-error" : undefined}
                  aria-invalid={errors.subject ? "true" : "false"}
                />
                {errors.subject && (
                  <p id="subject-error" className="text-sm text-destructive">{errors.subject.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  placeholder="Your message..."
                  rows={5}
                  {...register("message", { required: "Message is required" })}
                  aria-describedby={errors.message ? "message-error" : undefined}
                  aria-invalid={errors.message ? "true" : "false"}
                />
                {errors.message && (
                  <p id="message-error" className="text-sm text-destructive">{errors.message.message}</p>
                )}
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isSubmitting}
                aria-label="Send message"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
          
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-xl font-semibold mb-4">Connect With Me</h3>
              <p className="text-muted-foreground mb-6">
                Prefer to connect on social media? Find me on these platforms:
              </p>
              
              <div className="flex flex-wrap gap-4">
                {socialLinks.map((link) => (
                  <Button 
                    key={link.name} 
                    variant="outline" 
                    size="icon" 
                    asChild
                  >
                    <a 
                      href={link.href} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      aria-label={`Connect on ${link.name}`}
                    >
                      {link.icon}
                    </a>
                  </Button>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold mb-4">Quick Response</h3>
              <p className="text-muted-foreground">
                I typically respond to messages within 24-48 hours. For urgent inquiries, 
                please reach out via Telegram or LinkedIn for the fastest response.
              </p>
            </div>
          </motion.div>
        </div>
        
        <div className="mt-16 text-center border-t pt-6">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Sakthiish Vijayadass. All rights reserved.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Built with Next.js, Tailwind CSS, and Vercel.
          </p>
        </div>
      </div>
    </footer>
  )
}
